import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { PageHeader } from "../components/common";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Sparkles, Loader2, Send } from "lucide-react";

export default function AIAssistantPage() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text:
        "Bonjour. I am Marianne. Tell me about your legal matter, or ask me to suggest a template, summarize facts, or explain a legal concept. (Educational assistance — not legal advice.)",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    if (!input.trim()) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry — I could not respond. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const suggest = async (prompt) => {
    setInput(prompt);
  };

  return (
    <div data-testid="ai-assistant-page">
      <PageHeader
        eyebrow="L'Assistante"
        title="Ask Marianne"
        description="Your AI legal companion. Powered by Claude Sonnet — bound to precision and plain language."
      />

      <div className="grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 border border-[#E8E3F0] bg-white flex flex-col h-[70vh]">
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${i}`}>
                <div className={`max-w-[85%] p-4 border text-[15px] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#2A0F5A] text-[#FAFAF8] border-[#2A0F5A]" : "bg-[#FAFAF8] border-[#E8E3F0]"}`}>
                  {m.role === "assistant" && <div className="label-eyebrow text-[#C5A059] mb-2">Marianne</div>}
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-sm text-[#4A5568]" data-testid="ai-loading">
                <Loader2 size={14} className="animate-spin" /> Marianne is deliberating…
              </div>
            )}
            <div ref={endRef} />
          </div>
          <div className="border-t border-[#E8E3F0] p-4 flex gap-2">
            <Textarea
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Describe your matter, or ask a question…"
              className="rounded-none border-[#E8E3F0] bg-white focus-visible:ring-0 focus-visible:border-[#2A0F5A] resize-none"
              data-testid="ai-input"
            />
            <Button onClick={send} disabled={loading} className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="ai-send-btn">
              <Send size={14} />
            </Button>
          </div>
        </div>

        <div className="border border-[#E8E3F0] bg-white p-5">
          <div className="label-eyebrow mb-3">Try asking</div>
          <div className="space-y-2 text-sm">
            {[
              "Draft a demand letter for an unpaid invoice of $2,500.",
              "What is an affidavit and when do I need one?",
              "Summarise the essentials of a non-disclosure agreement.",
              "Give me a checklist before I file a small claims case.",
              "Explain the difference between a notice and a lawsuit.",
            ].map((p, i) => (
              <button
                key={i}
                onClick={() => suggest(p)}
                className="w-full text-left p-3 border border-[#E8E3F0] hover:border-[#2A0F5A] transition-colors"
                data-testid={`ai-suggest-${i}`}
              >
                {p}
              </button>
            ))}
          </div>
          <div className="mt-6 text-xs text-[#4A5568] leading-relaxed flex gap-2">
            <Sparkles size={12} className="text-[#C5A059] mt-0.5 shrink-0" />
            Marianne's responses are procedural and educational — not a substitute for a licensed lawyer.
          </div>
        </div>
      </div>
    </div>
  );
}
